/**
 * i18n Configuration for Multi-language Support
 * Requirement 49: Multi-language support for questionnaire
 *
 * Supported languages (10言語):
 * - ja: Japanese (default)
 * - en: English
 * - id: Indonesian (Bahasa Indonesia)
 * - mn: Mongolian (Монгол хэл)
 * - ne: Nepali (नेपाली)
 * - vi: Vietnamese (Tiếng Việt)
 * - ar: Arabic (العربية)
 * - bn: Bengali (বাংলা)
 * - zh: Chinese (中文)
 * - es: Spanish (Español)
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import jaTranslations from './locales/ja.json';
import enTranslations from './locales/en.json';
import idTranslations from './locales/id.json';
import mnTranslations from './locales/mn.json';
import neTranslations from './locales/ne.json';
import viTranslations from './locales/vi.json';
import arTranslations from './locales/ar.json';
import bnTranslations from './locales/bn.json';
import zhTranslations from './locales/zh.json';
import esTranslations from './locales/es.json';

/**
 * Supported languages for the questionnaire (10言語)
 */
export const SUPPORTED_LANGUAGES = [
  { code: 'ja', label: '日本語', nativeLabel: '日本語' },
  { code: 'en', label: 'English', nativeLabel: 'English' },
  { code: 'id', label: 'Indonesian', nativeLabel: 'Bahasa Indonesia' },
  { code: 'mn', label: 'Mongolian', nativeLabel: 'Монгол хэл' },
  { code: 'ne', label: 'Nepali', nativeLabel: 'नेपाली' },
  { code: 'vi', label: 'Vietnamese', nativeLabel: 'Tiếng Việt' },
  { code: 'ar', label: 'Arabic', nativeLabel: 'العربية' },
  { code: 'bn', label: 'Bengali', nativeLabel: 'বাংলা' },
  { code: 'zh', label: 'Chinese', nativeLabel: '中文' },
  { code: 'es', label: 'Spanish', nativeLabel: 'Español' },
] as const;

export type SupportedLanguageCode = (typeof SUPPORTED_LANGUAGES)[number]['code'];

/**
 * Get preferred language from localStorage
 */
const getStoredLanguage = (): string => {
  try {
    return localStorage.getItem('preferredLanguage') || 'ja';
  } catch {
    return 'ja';
  }
};

/**
 * Save preferred language to localStorage
 */
export const setStoredLanguage = (lang: string): void => {
  try {
    localStorage.setItem('preferredLanguage', lang);
  } catch {
    // localStorage not available
  }
};

/**
 * Initialize i18n
 */
i18n.use(initReactI18next).init({
  resources: {
    ja: { translation: jaTranslations },
    en: { translation: enTranslations },
    id: { translation: idTranslations },
    mn: { translation: mnTranslations },
    ne: { translation: neTranslations },
    vi: { translation: viTranslations },
    ar: { translation: arTranslations },
    bn: { translation: bnTranslations },
    zh: { translation: zhTranslations },
    es: { translation: esTranslations },
  },
  lng: getStoredLanguage(),
  fallbackLng: 'ja',
  interpolation: {
    escapeValue: false, // React already escapes
  },
  react: {
    useSuspense: false, // Disable suspense for better UX
  },
});

// Listen for language changes and save to localStorage
i18n.on('languageChanged', (lng) => {
  setStoredLanguage(lng);
  // Update document language attribute for accessibility
  document.documentElement.lang = lng;
});

export default i18n;
